import React, { useMemo } from 'react';
import {
  Box,
  Button,
  Flex,
  Icon,
  Input,
  InputGroup,
  InputRightElement,
  Select,
  Text,
  VisuallyHidden,
} from '@chakra-ui/react';
import FormElement from '@components/presentational/Form/FormElement';
import { PiCaretDownThin, PiMagnifyingGlassThin } from 'react-icons/pi';
import resourceTranslations from '@/translations/resource.fr.yml';
import { useRouter } from 'next/router';

export function getRouteFromCategoryValue(value) {
  switch (value) {
    case 'category.fle_culture':
      return '/ressources/themes/fle-culture';
    case 'category.fle_communication':
      return '/ressources/themes/fle-communication';
    case 'category.french_university':
      return '/ressources/themes/francais-universitaire';
    case 'category.french_professional':
      return '/ressources/themes/francais-professionnel';
    case 'category.dnl':
      return '/ressources/themes/disciplines-non-linguistiques';
    case 'category.alphabetization':
      return '/ressources/themes/alphabétisation';
    case 'category.french_training':
      return '/ressources/themes/certifications';
    case 'category.if_resources':
      return '/ressources/themes/les-ressources-de-l-institut-francais';
  }

  return null;
}

interface HeaderResourceFormProps {
  isRessourcesHomePageMobileOnly?: boolean;
  categories: {
    value: string;
    label: string;
    count: number;
  }[];
  search?: string;
  setSearch?: Function;
}

export default function HeaderResourceForm({
  isRessourcesHomePageMobileOnly = false,
  categories,
  search = '',
  setSearch,
}: HeaderResourceFormProps) {
  const CECRLevels = useMemo(
    () =>
      Object.entries(resourceTranslations.cecr_level).map(entry => {
        return {
          value: entry[0] as string,
          label: entry[1] as string,
        };
      }),
    [resourceTranslations]
  );
  const router = useRouter();

  const levelValue = useMemo<string>(() => {
    if (!router.query?.CECRLevel) return 'all';
    return router.query?.CECRLevel as string;
  }, [router.query]);

  const themeValue = useMemo<string>(() => {
    if (!router.query?.category) return 'all';
    return router.query?.category as string;
  }, [router.query]);

  return (
    <Box
      width={'100%'}
      display={{ md: isRessourcesHomePageMobileOnly ? 'none' : 'flex' }}
      gap={{ md: '1.25rem' }}
      padding={isRessourcesHomePageMobileOnly ? '0 1rem 1rem' : null}
      bg={isRessourcesHomePageMobileOnly ? 'tuile.25' : null}
    >
      {isRessourcesHomePageMobileOnly && (
        <Text mb="1rem" display={{ base: 'none', md: 'block' }}>
          Rechercher une ressource
        </Text>
      )}
      <Flex
        gap={{
          base: '1rem',
          md: '1.25rem',
        }}
        flexWrap={{
          base: 'nowrap',
          md: 'revert',
        }}
        pt={{
          base: isRessourcesHomePageMobileOnly ? 4 : 'revert',
          md: 'revert',
        }}
      >
        <FormElement label="Thèmes" isLabelVisuallyHidden>
          <Select
            aria-label="Thèmes"
            mb={{ base: '1rem', md: 0 }}
            size={{ base: 'md', md: 'lg' }}
            color={{ md: 'black.50' }}
            icon={<Icon as={PiCaretDownThin} mr=".25em" />}
            fontSize=".8125rem"
            value={themeValue}
            width={{ sm: '30rem' }}
            maxWidth={{ md: '39vw' }}
            onChange={async e => {
              const route = getRouteFromCategoryValue(e.target.value);
              if (route) {
                await router.push(
                  {
                    pathname: '/ressources/recherche',
                    query: { category: e.target.value },
                  },
                  route
                );
              }
            }}
          >
            <option value="all">Thèmes</option>
            <optgroup>
              {categories.map((category, index) => (
                <Box as="option" key={index} value={category.value}>
                  {category.label} ({category.count.toLocaleString()})
                </Box>
              ))}
            </optgroup>
          </Select>
        </FormElement>
        <FormElement label="Niveaux" isLabelVisuallyHidden>
          <Select
            aria-label="Niveaux"
            mb={{ base: '1rem', md: 0 }}
            size={{ base: 'md', md: 'lg' }}
            color={{ md: 'black.50' }}
            icon={<Icon as={PiCaretDownThin} mr=".25em" />}
            fontSize=".8125rem"
            width={{ md: '7rem' }}
            value={levelValue}
            onChange={async e => {
              if (
                ['a11', 'a1', 'a2', 'b1', 'b2', 'c1', 'c2'].includes(
                  e.target.value
                )
              ) {
                await router.push(
                  {
                    pathname: '/ressources/recherche',
                    query: { CECRLevel: e.target.value },
                  },
                  '/ressources/niveaux/' + e.target.value
                );
              }
            }}
          >
            <option value="all">Niveaux</option>
            <optgroup>
              {CECRLevels.map((level, index) => (
                <Box as="option" key={index} value={level.value}>
                  {level.label}
                </Box>
              ))}
            </optgroup>
          </Select>
        </FormElement>
      </Flex>
      <FormElement label="Rechercher une ressource" isLabelVisuallyHidden>
        <InputGroup size={{ base: 'md', md: 'lg' }}>
          <Input
            pr={{ base: '3.5rem', md: '3.5rem' }}
            type="search"
            placeholder="Rechercher une ressource"
            aria-label="Rechercher une ressource"
            fontSize=".8125rem"
            value={search}
            onChange={e => {
              setSearch(e.target.value);
            }}
          />
          <InputRightElement width={{ base: '2.5rem', md: '3rem' }}>
            <Button
              p={{ base: '.25rem', md: '.4rem' }}
              aspectRatio="1 / 1"
              minWidth="revert"
              height="auto"
              variant="tileOutline2"
              onClick={async () => {
                await router.push(
                  { pathname: '/ressources/recherche', query: { s: search } },
                  '/ressources/recherche'
                );
              }}
            >
              <VisuallyHidden>Lancer la recherche</VisuallyHidden>
              <Icon as={PiMagnifyingGlassThin} boxSize="1.5rem" />
            </Button>
          </InputRightElement>
        </InputGroup>
      </FormElement>
    </Box>
  );
}
